import {
  Button,
  Flex,
  Table,
  type TableColumnType,
  Tooltip,
  message,
} from 'antd';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAdminAccountsController_getAccounts } from '@api-client/generated/AdminAccountsController/getAccounts';
import { useAdminTransactionController_findAll } from '@api-client/generated/AdminTransactionController/findAll';
import { useAdminTransactionController_toExcel } from '@api-client/generated/AdminTransactionController/toExcel';
import { Schemas } from '@api-client/generated/types';
import { IconClose, IconFileOk, IconMissedFile, IconXLS } from '@assets';
import { ArchivedTag } from '@components';
import { Amount, FilterConstructor } from '@entities';
import {
  useCurrentCompany,
  useCurrentCompanyId,
  useInfiniteScrollByPage,
} from '@hooks';
import { captureException } from '@sentry/react';
import { getSearchParams, paginationNormalizer } from '@utils';

import { getTransactionContactName } from '../transactionContactName';
import * as S from './styled';

type Transaction = Schemas.Transaction;
type FilterOptions = Record<string, unknown>;

const ARCHIVED_SUFFIX = ' (Archived)';

type TransactionsContentProps = {
  title?: ReactNode;
  withSearchParams?: boolean;
  paginationCurrentPage?: number;
  externalCompanyId?: string;
  onPagination?: (metaData: Schemas.PaginationMetadata) => void;
  onFilter?: (options: FilterOptions) => void;
};

const columns: TableColumnType<Transaction>[] = [
  {
    key: 'hasDocuments',
    dataIndex: 'hasDocuments',
    title: '',
    width: 50,
    render: (hasDocuments, record) => {
      if (record.isFileless) {
        return null;
      }

      if (hasDocuments) {
        return (
          <Tooltip title="Payment document attached" placement="right">
            <S.FileOkIconWrapper>
              <IconFileOk />
            </S.FileOkIconWrapper>
          </Tooltip>
        );
      }

      return (
        <Tooltip title="Missed payment document" placement="right">
          <S.MissedFileIconWrapper>
            <IconMissedFile />
          </S.MissedFileIconWrapper>
        </Tooltip>
      );
    },
  },
  {
    key: 'amount',
    dataIndex: 'amount',
    title: 'Amount',
    align: 'right',
    width: 140,
    render: (amount, record) => (
      <Amount amount={amount} currencyCode={record.currencyCode} />
    ),
  },
  {
    key: 'contact',
    dataIndex: 'contact',
    title: 'Contact',
    width: 150,
    ellipsis: true,
    render: (_contact, record) => getTransactionContactName(record),
  },
  {
    key: 'details',
    dataIndex: 'details',
    title: 'Purpose of payment',
    ellipsis: true,
  },
  {
    key: 'account',
    dataIndex: 'account',
    title: 'Bank',
    width: 150,
    ellipsis: true,
    render: (account: Schemas.Account) =>
      account.connection?.bank?.name || account.accountName,
  },
  {
    key: 'bookingDate',
    dataIndex: 'bookingDate',
    title: 'Date',
    width: 110,
    render: (date) => format(date, 'dd.MM.yyyy'),
  },
];

type BuildExportFilenameParams = {
  companyName?: string;
  dateFrom?: string;
  dateTo?: string;
  term?: string;
  accountName?: string;
};

const buildExportFilename = ({
  companyName,
  dateFrom,
  dateTo,
  term,
  accountName,
}: BuildExportFilenameParams): string => {
  const parts: string[] = ['transactions'];

  if (companyName) {
    parts.push(companyName.replace(/[^a-zA-Z0-9]/g, '_'));
  }

  if (dateFrom && dateTo) {
    parts.push(`${dateFrom}_to_${dateTo}`);
  } else if (dateFrom) {
    parts.push(`from_${dateFrom}`);
  } else if (dateTo) {
    parts.push(`to_${dateTo}`);
  }

  if (accountName) {
    parts.push(accountName.replace(/[^a-zA-Z0-9]/g, '_'));
  }

  if (term) {
    parts.push(`search_${term.replace(/[^a-zA-Z0-9]/g, '_')}`);
  }

  return `${parts.join('_')}.xlsx`;
};

const TransactionsContent: FC<TransactionsContentProps> = ({
  title,
  withSearchParams = true,
  paginationCurrentPage,
  externalCompanyId,
  onPagination,
  onFilter,
}) => {
  const accountCompanyId = useCurrentCompanyId();
  const companyId = externalCompanyId || accountCompanyId;
  const { data: company } = useCurrentCompany();

  const { search } = useLocation();
  const navigate = useNavigate();

  const searchParams = getSearchParams<{ [key: string]: string }>(search);

  const [filterOptions, setFilterOptions] =
    useState<FilterOptions>(searchParams);

  const [transactionsList, setTransactionsList] = useState<Transaction[]>([]);
  const [isLastPage, setIsLastPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  const { currentPage: currentPageByScroll, resetScroll } =
    useInfiniteScrollByPage({ isLastPage });

  const { data, isPending: loading } = useAdminTransactionController_findAll({
    params: {
      companyId: companyId!,
      page: paginationCurrentPage || currentPageByScroll,
      ...filterOptions,
    },
  });
  useEffect(
    () => {
      if (data) {
        const { metadata, records } = data;

        if (paginationCurrentPage) {
          onPagination?.(metadata);
          onFilter?.(filterOptions);

          setTransactionsList(records);
        } else {
          const normalizeTransactions = paginationNormalizer<Transaction>({
            prevData: transactionsList,
            scrollPage: currentPageByScroll,
            setCurrentPage,
            currentPage,
            records,
            metadata,
          });

          if (metadata.currentPage === metadata.totalPages) {
            setIsLastPage(true);
          }

          if (normalizeTransactions) {
            setTransactionsList(normalizeTransactions);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const { refetch: fetchExcel } = useAdminTransactionController_toExcel({
    params: {
      companyId: companyId!,
      page: currentPageByScroll,
      ...filterOptions,
    },
    config: {
      // Do not fetch automatically
      enabled: false,
    },
  });

  const { data: accounts } = useAdminAccountsController_getAccounts({
    params: {
      companyId: companyId!,
    },
  });

  useEffect(
    () => {
      resetScroll();
      setIsLastPage(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterOptions]
  );

  const handleDownloadExcel = async () => {
    setIsExcelLoading(true);

    const dateFrom = filterOptions.dateFrom as string | undefined;
    const dateTo = filterOptions.dateTo as string | undefined;
    const term = filterOptions.term as string | undefined;
    const accountId = filterOptions.accountId as string | undefined;

    const accountName = accountId
      ? accounts?.find((account) => account.id === accountId)?.name
      : undefined;

    const filename = buildExportFilename({
      companyName: company?.name,
      dateFrom,
      dateTo,
      term,
      accountName,
    });

    try {
      const { data: blob, error } = await fetchExcel();

      if (error) {
        throw error;
      }

      if (!blob) {
        throw new Error('No data received from export');
      }

      saveAs(blob, filename);
    } catch (error) {
      message.error('Failed to export transactions. Please try again.');
      captureException(error, {
        level: 'error',
        tags: { feature: 'transactions-export' },
        extra: {
          companyId,
          filterOptions,
        },
      });
    } finally {
      setIsExcelLoading(false);
    }
  };

  return (
    <Flex gap={24} vertical>
      {title}

      <FilterConstructor<Transaction>
        controls={{
          left: [
            {
              type: 'search',
              formName: 'term',
              params: {
                clearIcon: <IconClose width={16} height={16} />,
                allowClear: true,
              },
            },
          ],
          right: [
            {
              type: 'range-picker',
              formName: 'date',
            },
            {
              type: 'select',
              formName: 'accountId',
              params: {
                width: 180,
                placeholder: 'Bank',
                'aria-label': 'Bank',
                virtual: false,
                popupMatchSelectWidth: false,
                options: accounts?.map((account) => ({
                  label: (
                    <S.AccountLabel>
                      {account.isArchived && <ArchivedTag />}
                      <S.AccountName>{account.name}</S.AccountName>
                    </S.AccountLabel>
                  ),
                  title: account.isArchived
                    ? `${account.name}${ARCHIVED_SUFFIX}`
                    : account.name,
                  'aria-label': account.isArchived
                    ? `${account.name}${ARCHIVED_SUFFIX}`
                    : account.name,
                  value: account.id,
                })),
              },
            },
            {
              type: 'custom-component',
              component: (
                <Button
                  icon={<IconXLS />}
                  type="secondary"
                  size="large"
                  onClick={handleDownloadExcel}
                  loading={isExcelLoading}
                  disabled={transactionsList.length === 0}
                />
              ),
            },
          ],
        }}
        onChange={setFilterOptions}
        withSearchParams={withSearchParams}
      />

      <S.TableContainer>
        <Table
          rowKey={({ id }) => id}
          onRow={({ id }) => ({
            onClick: () => navigate(id),
          })}
          dataSource={transactionsList}
          columns={columns}
          loading={loading}
          pagination={false}
          scroll={{ x: 720 }}
        />
      </S.TableContainer>
    </Flex>
  );
};

export default TransactionsContent;
